import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { API_BASE_URL } from '@/config';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  Clock,
  Home,
  Loader2,
  MapPin,
  Search,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface Gym {
  id: string;
  name: string;
  city: string | null;
  country_code: string | null;
  description: string | null;
  logo_url: string | null;
  require_approval: boolean;
  member_count: number;
  track_count: number;
}

interface JoinRequest {
  id: string;
  status: string;
  message: string | null;
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  affiliate_id: string;
  gym_name: string;
  city: string | null;
  country_code: string | null;
}

// Use centralized API_BASE_URL (already includes /api) to avoid double /api in production
const API_URL = API_BASE_URL;

export default function FindGym() {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Gym[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isJoining, setIsJoining] = useState<string | null>(null);
  const [myRequests, setMyRequests] = useState<JoinRequest[]>([]);

  // Fetch user's existing join requests when signed in
  useEffect(() => {
    if (isSignedIn) {
      fetchMyRequests();
    }
  }, [isSignedIn]);

  const fetchMyRequests = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/affiliates/directory/requests/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMyRequests(data.data?.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSearching(true);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      params.append('limit', '20');

      const response = await fetch(`${API_URL}/affiliates/directory?${params}`);

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.data?.gyms || []);
      } else {
        toast.error('Failed to search gyms');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to connect to server');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleJoinGym = async (gym: Gym) => {
    if (!isSignedIn) {
      // Store selected gym and redirect to register
      sessionStorage.setItem('selectedGym', JSON.stringify(gym));
      navigate(`/register?redirect=/find-gym&gymId=${gym.id}`);
      return;
    }

    // Check if already requested
    const existingRequest = myRequests.find((r) => r.affiliate_id === gym.id);
    if (existingRequest) {
      if (existingRequest.status === 'pending') {
        toast.info('You already have a pending request for this gym');
      } else if (existingRequest.status === 'rejected') {
        toast.error(
          `Your previous request was rejected: ${existingRequest.rejection_reason || 'No reason given'}`
        );
      }
      return;
    }

    setIsJoining(gym.id);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/affiliates/directory/${gym.id}/join`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.data?.type === 'joined') {
          toast.success(data.data.message || `You have joined ${gym.name}!`);
          // Refresh to show member status
          navigate('/member');
        } else if (data.data?.type === 'request') {
          toast.success(data.data.message || 'Join request submitted!');
          fetchMyRequests(); // Refresh requests
        }
      } else if (response.status === 409) {
        toast.info(data.error || 'You are already a member or have a pending request');
        fetchMyRequests();
      } else {
        toast.error(data.error || 'Failed to join gym');
      }
    } catch (error) {
      console.error('Join error:', error);
      toast.error('Failed to connect to server');
    } finally {
      setIsJoining(null);
    }
  };

  const cancelRequest = async (requestId: string) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/affiliates/directory/requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Request cancelled');
        fetchMyRequests();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to cancel request');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to connect to server');
    }
  };

  const getGymStatus = (gymId: string): 'none' | 'pending' | 'approved' | 'rejected' => {
    const request = myRequests.find((r) => r.affiliate_id === gymId);
    if (!request) return 'none';
    return request.status as 'pending' | 'approved' | 'rejected';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-blue-500/10 p-4">
                <Search className="h-10 w-10 text-blue-500" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold mb-2">Find My Gym</h1>
            <p className="text-muted-foreground text-lg">
              Search for your gym and join their ErgShoot community
            </p>
          </div>

          {/* My Pending Requests */}
          {isSignedIn && myRequests.filter((r) => r.status === 'pending').length > 0 && (
            <Card className="mb-8 border-primary/50 animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Pending Requests
                </CardTitle>
                <CardDescription>Your gym membership requests awaiting approval</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {myRequests
                  .filter((r) => r.status === 'pending')
                  .map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{request.gym_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {request.city && `${request.city}, `}
                          {request.country_code} - Requested{' '}
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => cancelRequest(request.id)}>
                        Cancel
                      </Button>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          {/* Search Form */}
          <Card className="mb-8 animate-fade-in">
            <CardHeader>
              <CardTitle>Search for Your Gym</CardTitle>
              <CardDescription>Enter your gym name to find and join</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="CrossFit Austin, ErgZone..."
                    className="pl-10"
                  />
                </div>
                <Button type="submit" disabled={isSearching}>
                  {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-2">
                Leave empty and click Search to browse all gyms
              </p>
            </CardContent>
          </Card>

          {/* Search Results */}
          {isSearching && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {!isSearching && hasSearched && searchResults.length === 0 && (
            <Card className="animate-fade-in">
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No gyms found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? `We couldn't find any gyms matching "${searchQuery}".`
                    : 'No gyms are currently listed in the directory.'}
                </p>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Try a different search, or:</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/apply/affiliate">
                      <Button variant="outline">
                        <Building2 className="h-4 w-4 mr-2" />
                        Start Your Gym on ErgShoot
                      </Button>
                    </Link>
                    <Link to="/signup/individual">
                      <Button variant="outline">Train at Home Instead</Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!isSearching && searchResults.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Found {searchResults.length} gym{searchResults.length !== 1 ? 's' : ''}
                {searchQuery && ` matching "${searchQuery}"`}
              </p>
              {searchResults.map((gym, index) => {
                const status = getGymStatus(gym.id);
                return (
                  <Card
                    key={gym.id}
                    className="hover:border-primary/50 transition-all animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="rounded-lg bg-primary/10 p-3">
                            {gym.logo_url ? (
                              <img
                                src={gym.logo_url}
                                alt={gym.name}
                                className="h-6 w-6 object-contain"
                              />
                            ) : (
                              <Building2 className="h-6 w-6 text-primary" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{gym.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {(gym.city || gym.country_code) && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {gym.city && `${gym.city}, `}
                                  {gym.country_code}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {gym.member_count} members
                              </span>
                            </div>
                            {gym.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                {gym.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {status === 'pending' && (
                            <span className="flex items-center gap-1 text-sm text-primary">
                              <Clock className="h-4 w-4" />
                              Pending
                            </span>
                          )}
                          {status === 'approved' && (
                            <span className="flex items-center gap-1 text-sm text-green-500">
                              <CheckCircle className="h-4 w-4" />
                              Member
                            </span>
                          )}
                          {status === 'none' && (
                            <Button
                              onClick={() => handleJoinGym(gym)}
                              disabled={isJoining === gym.id}
                            >
                              {isJoining === gym.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : gym.require_approval ? (
                                'Request to Join'
                              ) : (
                                'Join'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Not Searched Yet */}
          {!hasSearched && (
            <div
              className="grid gap-4 md:grid-cols-2 animate-fade-in"
              style={{ animationDelay: '100ms' }}
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Already know your gym?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Search above to find and join your gym's ErgShoot community. You'll get access
                    to their programming and leaderboards.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Gym not on ErgShoot?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Tell your gym owner about ErgShoot, or train at home with an individual account.
                  </p>
                  <Link to="/signup/individual">
                    <Button variant="outline" size="sm">
                      Train at Home
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Help */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              Can't find your gym?{' '}
              <a href="mailto:support@ergshoot.com" className="text-primary hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
